import React, { useContext, useEffect, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import "./TrainerSlots.css";

const BRANCHES = [
  { id: "all", name: "All branches" },
  { id: "15", name: "F-11" },
  { id: "1", name: "G-13" },
];

const CHOICES = [
  { status: "unmarked", label: "Unmarked" },
  { status: "free", label: "Free" },
  { status: "reserved_half", label: "Reserved half" },
  { status: "reserved_full", label: "Reserved full" },
];

const TrainerSlots = () => {
  const { state } = useContext(NoteState);
  const role = String(state.role ?? "");
  const isTrainer = role === "9";
  const [branchId, setBranchId] = useState(role === "1" ? "all" : String(state.branch_id ?? "all"));
  const [trainerId, setTrainerId] = useState("all");
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [assignSlot, setAssignSlot] = useState(null);
  const [clientQuery, setClientQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [searching, setSearching] = useState(false);

  const load = async (nextBranch = branchId, nextTrainer = trainerId) => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (!isTrainer) {
        if (nextBranch && nextBranch !== "all") params.branch_id = nextBranch;
        if (nextTrainer && nextTrainer !== "all") params.staff_id = nextTrainer;
      }
      const response = await UserServices.trainerSlots(params);
      const data = response.data || {};
      setBoard(data);
      if (!isTrainer && nextTrainer === "all") {
        setTrainerOptions(data.trainers || []);
      }
    } catch (err) {
      setBoard(null);
      setError(err?.response?.data?.message || "Could not load slots.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(branchId, "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!assignSlot) return undefined;
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await UserServices.trainerSlotClients({
          q: clientQuery,
          branch_id: assignSlot.branchId || undefined,
        });
        setClients(response.data?.data || []);
      } catch (err) {
        setClients([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [assignSlot, clientQuery]);

  const refresh = () => load(branchId, trainerId);

  const markStatus = async (slot, status) => {
    if (slot.status === status) {
      setSuccess(slot.label + " is already " + CHOICES.find((choice) => choice.status === status).label + ".");
      return;
    }
    setBusyId(slot.id);
    setError("");
    setSuccess("");
    try {
      const response = await UserServices.trainerSlotStatus(slot.id, status);
      setSuccess(response.data?.message || "Slot updated.");
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not update the slot.");
    } finally {
      setBusyId(null);
    }
  };

  const assign = async (client) => {
    if (!assignSlot) return;
    setBusyId(assignSlot.slot.id);
    setError("");
    setSuccess("");
    try {
      const response = await UserServices.trainerSlotAssign(assignSlot.slot.id, client.id);
      setSuccess(response.data?.message || "Slot assigned.");
      setAssignSlot(null);
      setClientQuery("");
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not assign the slot.");
    } finally {
      setBusyId(null);
    }
  };

  const trainers = board?.trainers || [];
  const totals = board?.totals || {};
  const mine = trainers[0];

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4 roster-page">
            <div className="roster-head">
              <h2>{isTrainer ? "Slots Management" : "Slots Management (Trainers)"}</h2>
              <p>
                {isTrainer
                  ? "Hours start unmarked, so sales cannot see them. Mark Free or Reserved half to offer that hour. Reserved full keeps it off the sales list."
                  : "Only hours a trainer marked full free or half free are listed here."}
              </p>
            </div>

            {success ? <p className="roster-success">{success}</p> : null}
            {error ? <p className="roster-error">{error}</p> : null}
            {loading ? <p className="roster-note">Loading slots...</p> : null}

            {isTrainer && !loading && mine ? (
              <section className="roster-card">
                <div className="roster-summary">
                  <span>Unmarked<strong>{mine.summary?.unmarked || 0}</strong></span>
                  <span>Full free<strong>{mine.summary?.full_free || 0}</strong></span>
                  <span>Half free<strong>{mine.summary?.half_free || 0}</strong></span>
                  <span>Reserved full<strong>{mine.summary?.reserved_full || 0}</strong></span>
                </div>
                {mine.slots?.length ? (
                  <div className="roster-hours">
                    {mine.slots.map((slot) => (
                      <article key={slot.id} className={`roster-hour is-${slot.status}`}>
                        <strong>{slot.label}</strong>
                        <span>{slot.status === "assigned" ? "Assigned by sales" : (CHOICES.find((choice) => choice.status === slot.status)?.label || "Unmarked")}</span>
                        {slot.client_name ? <em>{slot.client_name}</em> : null}
                        <div className="roster-choices">
                          {CHOICES.map((choice) => (
                            <button
                              type="button"
                              key={choice.status}
                              className={slot.status === choice.status ? "is-current" : ""}
                              disabled={busyId === slot.id}
                              onClick={() => markStatus(slot, choice.status)}
                            >
                              {choice.label}
                            </button>
                          ))}
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="roster-note">No duty hours are set, so there are no slots yet.</p>
                )}
              </section>
            ) : null}

            {!isTrainer && !loading ? (
              <>
                <div className="roster-summary">
                  <span>Full free<strong>{totals.full_free || 0}</strong></span>
                  <span>Half free<strong>{totals.half_free || 0}</strong></span>
                  <span>Assigned<strong>{totals.assigned || 0}</strong></span>
                </div>
                <div className="roster-filters">
                  <label>
                    Branch
                    <select
                      className="form-select"
                      value={branchId}
                      onChange={(event) => {
                        const next = event.target.value;
                        setBranchId(next);
                        setTrainerId("all");
                        load(next, "all");
                      }}
                    >
                      {BRANCHES.map((branch) => (
                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Trainer
                    <select
                      className="form-select"
                      value={trainerId}
                      onChange={(event) => {
                        const next = event.target.value;
                        setTrainerId(next);
                        load(branchId, next);
                      }}
                    >
                      <option value="all">All trainers</option>
                      {trainerOptions.map((trainer) => (
                        <option key={trainer.id} value={trainer.id}>
                          {trainer.name}{trainer.branch_name ? ` · ${trainer.branch_name}` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="roster-scroll">
                  <table className="roster-list">
                    <thead>
                      <tr>
                        <th>Trainer</th>
                        <th>Branch</th>
                        <th>Free slots</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trainers.length === 0 ? (
                        <tr>
                          <td colSpan="3">No trainers for this selection.</td>
                        </tr>
                      ) : trainers.map((trainer) => {
                        const openSlots = (trainer.slots || []).filter((slot) =>
                          slot.availability === "full_free" || slot.availability === "half_free" || slot.availability === "assigned"
                        );
                        return (
                          <tr key={trainer.id}>
                            <td>
                              <strong>{trainer.name}</strong>
                              <div className="roster-muted">{trainer.uid || ""}</div>
                            </td>
                            <td>{trainer.branch_name || "—"}</td>
                            <td>
                              {openSlots.length === 0 ? <span className="roster-muted">No free slots</span> : null}
                              <div className="roster-chip-row">
                                {openSlots.map((slot) => (
                                  <button
                                    type="button"
                                    key={slot.id}
                                    className={`roster-chip is-${slot.availability}`}
                                    disabled={slot.availability === "assigned" || busyId === slot.id}
                                    onClick={() => {
                                      setClientQuery("");
                                      setClients([]);
                                      setAssignSlot({ trainer, slot, branchId: trainer.branch_id });
                                    }}
                                  >
                                    <span>{slot.label}</span>
                                    <strong>{slot.availability_label}</strong>
                                    {slot.client_name ? <em>{slot.client_name}</em> : null}
                                  </button>
                                ))}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </main>

      {assignSlot ? (
        <div className="roster-modal-backdrop" onClick={() => setAssignSlot(null)}>
          <div className="roster-modal" onClick={(event) => event.stopPropagation()}>
            <h3>Assign {assignSlot.slot.label}</h3>
            <p className="roster-note">
              {assignSlot.trainer.name} · {assignSlot.slot.availability_label}
            </p>
            <input
              className="form-control"
              placeholder="Search client name"
              value={clientQuery}
              onChange={(event) => setClientQuery(event.target.value)}
              autoFocus
            />
            <div className="roster-results">
              {searching ? <p className="roster-note">Searching...</p> : null}
              {!searching && clients.length === 0 ? <p className="roster-note">No clients found.</p> : null}
              {clients.map((client) => (
                <button type="button" key={client.id} onClick={() => assign(client)}>
                  {client.name}
                </button>
              ))}
            </div>
            <div className="roster-modal-actions">
              <button type="button" onClick={() => setAssignSlot(null)}>Close</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TrainerSlots;
